import reactUpdate from 'immutability-helper';
import _ from 'underscore';

import * as EventConstants from 'constants/EventConstants';
import AppDispatcher, {FluxAction} from 'lib/Dispatcher';
import Store from 'lib/Store';

import {Timer} from './TimerConstants';

const DEFAULT_TIMER_MS = 60 * 1000;
const TIMER_DEFAULTS = {
  inline: true,
  inlineCopy: false, // Need this to force audio to play
  originalMS: DEFAULT_TIMER_MS,
  startMS: DEFAULT_TIMER_MS,
  startMoment: null,
  playAudio: true,
};

interface State {
  timers: Array<Timer>;
  protocolTimers: Array<Timer>;
}
class TimerStore extends Store {
  timers!: State['timers'];
  protocolTimers!: State['protocolTimers'];

  resetState(): void {
    this.timers = []; // All currently active timers
    this.protocolTimers = []; // All timers associated with open protocols
  }

  getState(): State {
    return {
      timers: this.timers,
      protocolTimers: this.protocolTimers,
    };
  }

  getTimer(id: string): Timer | undefined {
    // eslint-disable-next-line no-restricted-syntax
    return _.find(this.timers, {id});
  }

  getProtocolTimer(id: string): Timer | undefined {
    // eslint-disable-next-line no-restricted-syntax
    return _.find(this.protocolTimers, {id});
  }

  getTrayIndex(timerId: string): number {
    // eslint-disable-next-line no-restricted-syntax
    return _.findIndex(this.timers, {id: timerId});
  }

  getProtocolIndex(timerId: string): number {
    // eslint-disable-next-line no-restricted-syntax
    return _.findIndex(this.protocolTimers, {id: timerId});
  }

  payloadHandler(action: FluxAction): boolean {
    switch (action.actionType) {
      case EventConstants.TIMER_UPDATE:
        this.updateTimer(action.timerId, action.options);
        this.emitChange();
        break;

      case EventConstants.TIMER_REMOVE:
        this.removeTimer(action.timerId);
        this.emitChange();
        break;

      default:
        break;
    }

    return true;
  }

  // Returns a new timer object but does NOT update the lists of timers
  newTimer(timerId: string, options: Partial<Timer>): Timer {
    const timer = _.defaults({}, options, TIMER_DEFAULTS);
    timer.id = timerId;
    timer.startMS = timer.originalMS;
    return timer;
  }

  // Creates a new timer and adds to list of protocolTimers
  addProtocolTimer(timerId: string, options: Partial<Timer>): void {
    if (this.getProtocolTimer(timerId) != null) {
      throw new Error(`Timer with id ${timerId} already exists`);
    }

    const timer = this.newTimer(timerId, options);
    this.protocolTimers = reactUpdate(this.protocolTimers, {$push: [timer]});

    // If starting, make sure it's in the list of active timers
    const starting = options.startMoment != null;
    const trayIndex = this.getTrayIndex(timerId);
    if (starting && trayIndex === -1) {
      this.timers = reactUpdate(this.timers, {$push: [timer]});
    }
  }

  // Updates the timer with the given timerId in protocolTimers
  updateTimer(timerId: string, options: Partial<Timer>): void {
    const trayIndex = this.getTrayIndex(timerId);
    if (trayIndex > -1) {
      this.updateTrayTimer(timerId, trayIndex, options);
    }

    const protocolIndex = this.getProtocolIndex(timerId);
    if (protocolIndex > -1) {
      this.updateProtocolTimer(timerId, protocolIndex, options);
    } else {
      this.addProtocolTimer(timerId, options);
    }
  }

  // Updates the timer with timerId at the given index position in timers with new options
  updateTrayTimer(timerId: string, index: number, options: Partial<Timer>): void {
    const timer = this.timers[index];
    const newTimer = reactUpdate(timer, {$merge: options});
    this.timers = reactUpdate(this.timers, {
      $splice: [[index, 1, newTimer]],
    });
  }

  // Updates the timer with timerId at the given index position in protocolTimers with new options
  updateProtocolTimer(timerId: string, index: number, options: Partial<Timer>): void {
    const timer = this.protocolTimers[index];
    const newTimer = reactUpdate(timer, {$merge: options});
    this.protocolTimers = reactUpdate(this.protocolTimers, {
      $splice: [[index, 1, newTimer]],
    });

    // If starting, make sure it's in the list of active timers
    const starting = options.startMoment != null;
    const trayIndex = this.getTrayIndex(timerId);
    if (starting && trayIndex === -1) {
      this.timers = reactUpdate(this.timers, {$push: [newTimer]});
    }

    // On timer reset, make sure it's not in the list of active timers
    if (!starting && trayIndex > -1 && newTimer.startMS > 0) {
      this.removeTrayTimer(timerId, trayIndex);
    }
  }

  // Removes the timer with the given timerId from protocolTimers
  removeTimer(timerId: string): void {
    const trayIndex = this.getTrayIndex(timerId);
    if (trayIndex > -1) {
      this.removeTrayTimer(timerId, trayIndex);
    }

    const protocolIndex = this.getProtocolIndex(timerId);
    if (protocolIndex > -1) {
      this.removeProtocolTimer(timerId, protocolIndex);
    }
  }

  // Removes the timer with timerId at the given index position from timers
  removeTrayTimer(timerId: string, index: number): void {
    this.timers = reactUpdate(this.timers, {
      $splice: [[index, 1]],
    });
  }

  // Removes the timer with timerId at the given index position from protocolTimers
  removeProtocolTimer(timerId: string, index: number): void {
    this.protocolTimers = reactUpdate(this.protocolTimers, {
      $splice: [[index, 1]],
    });
  }
}

export default new TimerStore(AppDispatcher);
